import { faScroll, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, router, InfiniteScroll } from "@inertiajs/react";
import { useRef, useState, useEffect } from "react";
import { handleTableScroll } from "../../../utils/infiniteScroll";
import { debounce } from "../Attachments/RecordsTable";

// Debounced router visit with 300ms delay
const debounceQueryVisit = debounce((query: string) => {
  router.visit('', {
    data: {
      query
    },
    preserveState: true,
    preserveScroll: true
  })
}, 300);

export function AdminAnnouncementCard({ announcement, previewedAnnouncementId }: any) {
  const [isHovered, setIsHovered] = useState(false);
  const backgroundImage = announcement.image_url

  const active = previewedAnnouncementId === announcement.id

  // Determine publication status
  const getPublicationStatus = () => {
    if (!announcement.published_at) {
      return { label: 'Non publié', color: '#dc2626', bgColor: '#fee2e2' } // red
    }

    const publishDate = new Date(announcement.published_at)
    const now = new Date()

    if (publishDate > now) {
      return { label: 'Planifié', color: '#d97706', bgColor: '#fef3c7' } // yellow
    }

    return { label: 'Publié', color: '#16a34a', bgColor: '#dcfce7' } // green
  }

  const status = getPublicationStatus()

  return (
    <Link
      href={`/admin/announcements/${announcement.id}`}
      preserveState={true}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 8px',
        border: '1px solid var(--color-base-300)',
        backgroundColor: active ? 'var(--color-neutral)' : isHovered ? 'var(--color-base-300)' : 'var(--color-base-200)',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'background-color 0.15s ease',
      }}
    >
      {/* Thumbnail or icon */}
      {backgroundImage ? (
        <img
          src={backgroundImage}
          alt=""
          style={{
            width: '24px',
            height: '24px',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <FontAwesomeIcon
          icon={faScroll}
          style={{
            width: '24px',
            fontSize: '14px',
            color: 'var(--color-primary)',
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      )}

      {/* Title */}
      <span
        className="text-sm font-semibold"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: active ? 'var(--color-white)' : 'var(--color-neutral)',
        }}
      >
        {announcement.title}
      </span>

      {/* Date */}
      <span
        style={{
          fontSize: '11px',
          color: active ? 'var(--color-white)' : 'var(--color-base-content)',
          opacity: 0.6,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {announcement.published_at
          ? new Date(announcement.published_at).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
          })
          : '—'}
      </span>

      {/* Status badge */}
      <span
        style={{
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: 600,
          color: status.color,
          backgroundColor: status.bgColor,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {status.label}
      </span>

      {/* Action links */}
      <span
        className="text-xs link link-primary"
        title="Gérer"
      >
        Gérer
      </span>
      {announcement.published_at && new Date(announcement.published_at) <= new Date() && (
        <Link
          href={`/announcements/${announcement.id}`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className="text-xs link link-primary"
          title="Voir"
        >
          Voir
        </Link>
      )}
    </Link>
  )
}

export default function AnnouncementsList({
  announcements,
  previewedAnnouncement,
  searchQuery = ''
}: any) {
  const infiniteScrollRef = useRef<any>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const previewedAnnouncementId = previewedAnnouncement?.id
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');

  // Handle '/' key press to focus search input and Escape to blur
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Focus search input on '/' key press
      if (e.key === '/' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Blur search input on Escape key press
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div style={{
      backgroundColor: 'white',
      height: '100%',
      border: '2px solid #ddd',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    }}>
      <div style={{
        width: '100%',
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #ddd',
        padding: '4px',
        fontWeight: 'bold',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <div>
          Announcements
          {announcements.metadata && (
            <span className="text-sm" style={{ marginLeft: '8px', fontWeight: 'normal' }}>
              {announcements.metadata.total} {announcements.metadata.total === 1 ? 'announcement' : 'announcements'}
            </span>
          )}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
        }}>
          <Link href="/admin/announcements">
            <button className="btn btn-sm btn-success btn-primary">
              New
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </Link>
        </div>
      </div>
      <div style={{
        padding: '4px',
        backgroundColor: 'white',
        borderBottom: '1px solid #ddd',
      }}>
        <label className="input input-sm">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="search"
            placeholder="Search"
            ref={searchInputRef}
            value={localSearchQuery}
            onChange={(e) => {
              const newQuery = e.target.value;
              setLocalSearchQuery(newQuery);
              debounceQueryVisit(newQuery);
            }}
          />
          <kbd className="kbd kbd-sm">/</kbd>
        </label>
      </div>
      <InfiniteScroll
        ref={infiniteScrollRef}
        data="announcements"
        manual
        className="hidden-scrollbar"
        style={{
          padding: '2px 4px',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}
        onScroll={(e: React.UIEvent<HTMLDivElement>) => handleTableScroll(e, infiniteScrollRef)}
      >
        {announcements.data.map((announcement: any) => (
          <AdminAnnouncementCard
            key={`announcement-${announcement.id}`}
            announcement={announcement}
            previewedAnnouncementId={previewedAnnouncementId}
          />
        ))}
        <Link
          href="/admin/announcements"
          className="text-xs link link-success"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '4px',
            marginTop: '2px',
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          New Announcement
        </Link>
        {announcements.metadata.has_more && (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => infiniteScrollRef.current.fetchNext()}
            >
              Load more
            </button>
          </div>
        )}
      </InfiniteScroll>
      {announcements.data.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>
          No announcements available
        </div>
      )}
    </div>
  )
}
